import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import type { ApiMetric } from '@/stores/diagnosticsStore'

interface TimingBreakdownBarProps {
	metric: ApiMetric
}

interface Segment {
	label: string
	value: number
	color: string
}

const formatMs = (ms: number): string => {
	if (ms === 0) {
		return '0 ms'
	}
	if (ms < 1) {
		return '<1 ms'
	}
	return `${Math.round(ms)} ms`
}

export const TimingBreakdownBar = ({ metric }: TimingBreakdownBarProps) => {
	const server = metric.ttfb ?? 0
	const transfer = metric.download ?? 0
	const other = Math.max(0, metric.duration - server - transfer)
	const segments: Segment[] = [
		{ label: 'Server', value: server, color: 'bg-success' },
		{ label: 'Transfer', value: transfer, color: 'bg-warning' },
		{ label: 'Other', value: other, color: 'bg-chart-series-4' },
	]

	const total = metric.duration > 0 ? metric.duration : 1

	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<div className='flex h-3 w-full rounded-sm overflow-hidden bg-muted/40'>
					{segments.map(seg => {
						const pct = (seg.value / total) * 100
						if (pct <= 0) {
							return null
						}
						return <div key={seg.label} className={seg.color} style={{ width: `${pct}%` }} />
					})}
				</div>
			</TooltipTrigger>
			<TooltipContent className='p-2 min-w-50'>
				<div className='flex flex-col gap-1'>
					{segments.map(seg => (
						<div key={seg.label} className='flex items-center justify-between gap-3'>
							<div className='flex items-center gap-2'>
								<span className={`inline-block size-2 rounded-sm ${seg.color}`} />
								<span>{seg.label}</span>
							</div>
							<span className='tabular-nums font-mono'>{formatMs(seg.value)}</span>
						</div>
					))}
					<div className='flex items-center justify-between gap-3 border-t border-border pt-1 mt-1 font-semibold'>
						<span>Total</span>
						<span className='tabular-nums font-mono'>{formatMs(metric.duration)}</span>
					</div>
				</div>
			</TooltipContent>
		</Tooltip>
	)
}
